import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import Flag from 'react-flagkit';
import { GoChevronDown } from 'react-icons/go';

import { BookingService } from '@/services';
import { sortCountriesByPriority } from '@/utils/countryPriority';

import TextInput, { type TextInputProps } from './TextInput';

export interface CountryOption {
  code: string;
  name: string;
  dialCode: string; // digits only
}

export interface PhoneNumberInputValue {
  country: CountryOption;
  dialCode: string; // with leading +
  national: string; // subscriber digits only
  e164: string; // +<dialCode><national>
}

export interface PhoneNumberInputProps
  extends Omit<TextInputProps, 'onChange' | 'value' | 'defaultValue' | 'type'> {
  value?: string; // controlled national part
  defaultValue?: string; // uncontrolled initial
  telPrefix: string; // REQUIRED: initial dial code (e.g. '+66')
  onChange?: (
    value: PhoneNumberInputValue,
    rawEvent?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onCountryChange?: (country: CountryOption) => void;
  countries?: CountryOption[]; // optional explicit list overrides dynamic fetch
  maxNationalLength?: number; // cap digits
  renderFlag?: (country: CountryOption) => React.ReactNode;
  prefixWidth?: number; // force reserved width
}

const buildValue = (
  country: CountryOption,
  national: string,
): PhoneNumberInputValue => {
  const dial = `+${country.dialCode}`;
  return {
    country,
    dialCode: dial,
    national,
    e164: dial + national,
  };
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value: controlledNational,
  defaultValue = '',
  telPrefix,
  onChange,
  onCountryChange,
  countries,
  maxNationalLength = 15,
  renderFlag,
  prefixWidth,
  required,
  label = 'Phone number',
  error,
  helperText,
  size,
  disabled,
  readOnly,
  className,
  ...rest
}) => {
  // Dynamic fetch states
  const [dynamicCountries, setDynamicCountries] = useState<
    CountryOption[] | null
  >(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countryError, setCountryError] = useState<string | null>(null);

  // Fetch countries if not provided
  useEffect(() => {
    if (countries) return; // user supplied
    if (dynamicCountries) return; // already fetched
    let cancelled = false;
    (async () => {
      try {
        setLoadingCountries(true);
        const fetched = await BookingService.getCountries();
        const prioritized = sortCountriesByPriority(fetched);
        if (!cancelled) setDynamicCountries(prioritized);
      } catch (e: unknown) {
        if (!cancelled) {
          setCountryError(
            e instanceof Error ? e.message : 'Failed to load countries',
          );
          setDynamicCountries([]);
        }
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countries, dynamicCountries]);

  const allCountries = useMemo(
    () => countries || dynamicCountries || [],
    [countries, dynamicCountries],
  );

  const initialCountry = useMemo(() => {
    if (!allCountries.length) return null;
    // Resolve country strictly from dial code in telPrefix
    const digits = telPrefix.replace(/[^0-9]/g, '');
    if (digits) {
      const byDial = allCountries.find((c) => c.dialCode === digits);
      if (byDial) return byDial;
    }
    return null; // no silent fallback to first country to surface data issues
  }, [allCountries, telPrefix]);

  const [country, setCountry] = useState<CountryOption | null>(initialCountry);
  useEffect(() => {
    if (!country && initialCountry) setCountry(initialCountry);
  }, [country, initialCountry]);

  // Update country when telPrefix changes (e.g., when "use contact info" is toggled)
  useEffect(() => {
    if (initialCountry && country?.dialCode !== initialCountry.dialCode) {
      setCountry(initialCountry);
    }
  }, [initialCountry, country?.dialCode]);

  // Dev-time warning if provided countryCode not found after list loads
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      allCountries.length &&
      !initialCountry
    ) {
      console.warn(
        `[PhoneNumberInput] telPrefix "${telPrefix}" is not a valid dial code.`,
      );
    }
  }, [allCountries.length, initialCountry, telPrefix]);

  const [uncontrolledNational, setUncontrolledNational] =
    useState(defaultValue);
  const isControlled = controlledNational !== undefined;
  const national = isControlled ? controlledNational : uncontrolledNational;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const prefixRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (prefixRef.current) {
      const w = prefixRef.current.getBoundingClientRect().width;
      setMeasuredWidth((prev) => (prev && Math.abs(prev - w) < 1 ? prev : w));
    }
  }, [country, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const emitChange = (
    nextNational: string,
    nextCountry = country,
    rawEvent?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!nextCountry) return;
    onChange?.(buildValue(nextCountry, nextNational), rawEvent);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    let digits = e.target.value.replace(/\D/g, '');
    if (maxNationalLength > 0) digits = digits.slice(0, maxNationalLength);
    if (!isControlled) setUncontrolledNational(digits);
    if (e.target.value !== digits) e.target.value = digits;
    emitChange(digits);
  };

  const handleSelectCountry = (c: CountryOption) => {
    if (readOnly) return;

    setCountry(c);
    onCountryChange?.(c);
    emitChange(national, c);
    setOpen(false);
  };

  const countryDisplay = country ? `+${country.dialCode}` : '';
  const flagEl = country ? (
    renderFlag ? (
      renderFlag(country)
    ) : (
      <div className="flex h-4 w-6 items-center justify-center overflow-hidden rounded-sm">
        <Flag country={country.code} size={24} />
      </div>
    )
  ) : (
    <div className="bg-divider h-4 w-[21px] animate-pulse rounded-sm" />
  );

  const prefix = (
    <div
      ref={prefixRef}
      className="relative ml-[-8px] flex h-full items-center gap-1 pr-2"
    >
      <button
        type="button"
        disabled={disabled || readOnly || !allCountries.length}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-full items-center gap-1 focus:outline-none ${disabled ? 'text-border-disabled cursor-not-allowed' : readOnly ? 'text-secondary-text cursor-default' : 'text-primary-text cursor-pointer'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {flagEl}
        <GoChevronDown className="text-secondary-text" size={20} />
      </button>
      <span className="text-secondary-text leading-[24px] font-normal select-none">
        {countryDisplay}
      </span>
    </div>
  );

  const panelMaxHeight = 190; // ~4 items (4*40 + padding & gaps)

  return (
    <div ref={containerRef} className="relative w-full">
      <TextInput
        {...rest}
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        size={size}
        disabled={disabled || !country}
        readOnly={readOnly}
        prefixIcon={prefix}
        inputMode="tel"
        pattern="[0-9]*"
        value={national}
        onChange={handleNationalChange}
        className={className}
        prefixReservedWidth={
          prefixWidth ?? (measuredWidth ? Math.ceil(measuredWidth) + 4 : 90)
        }
        placeholderPaddingLeft={0}
        floatedLabelLeft={37}
      />
      {open && (
        <div
          ref={dropdownRef}
          className="shadow-dropdown absolute top-full left-0 z-50 w-full rounded-lg bg-white p-2"
          style={{ maxHeight: panelMaxHeight, height: panelMaxHeight }}
        >
          <ul
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent scrollbar-thumb-rounded-full flex h-full flex-col gap-1 overflow-auto"
            role="listbox"
          >
            {allCountries.map((c) => {
              const selected = country?.code === c.code;
              return (
                <li key={c.code} className="list-none">
                  <button
                    type="button"
                    onClick={() => handleSelectCountry(c)}
                    className={`text-primary-text flex h-10 w-full items-center gap-2 rounded-lg px-4 py-[6px] text-left text-sm leading-7 transition-colors md:hover:cursor-pointer ${selected ? 'bg-bg-selected' : 'hover:bg-bg-hover bg-white'}`}
                    role="option"
                    aria-selected={selected}
                  >
                    {renderFlag ? (
                      renderFlag(c)
                    ) : (
                      <div className="flex h-4 w-6 items-center justify-center overflow-hidden rounded-sm">
                        <Flag country={c.code} size={24} />
                      </div>
                    )}
                    <span className="truncate">
                      +{c.dialCode} ({c.name})
                    </span>
                  </button>
                </li>
              );
            })}
            {loadingCountries && (
              <li className="text-secondary-text px-4 py-2 text-xs leading-4">
                Loading...
              </li>
            )}
            {countryError && (
              <li className="text-error px-4 py-2 text-xs leading-4">
                {countryError}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;
